import Layout from "../components/layout/Layout";

export default function TermsPage() {
  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms &amp; Conditions</h1>
        <p className="text-sm text-slate-500 mb-8">Last updated: January 2025</p>

        <div className="prose prose-slate max-w-none">
          <p className="text-slate-600 mb-6">
            By using CheepSweepz Concierge (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;), you agree to be bound 
            by these Terms &amp; Conditions. Please read them carefully before using our services.
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">1. Nature of Service</h2>
            <p className="text-slate-600 mb-4">
              <strong>CheepSweepz Concierge is an import facilitation and concierge service.</strong> We help 
              customers purchase products from international marketplaces and handle the import process, including 
              customs clearance, shipping, and delivery.
            </p>
            <p className="text-slate-600">
              <strong>We are NOT a marketplace and do not sell inventory directly.</strong> We act as your agent 
              to purchase products on your behalf from third-party marketplaces and coordinate their delivery 
              to your location.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">2. Non-Affiliation Disclaimer</h2>
            <p className="text-slate-600">
              <strong>CheepSweepz Concierge is NOT affiliated with, endorsed by, or sponsored by</strong> Amazon, 
              eBay, AliExpress, TEMU, SHEIN, Walmart, Alibaba, or any other marketplace. All trademarks and brand 
              names are the property of their respective owners. We provide a service to help customers access 
              products from these platforms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">3. Pricing &amp; Final Price Lock</h2>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>All prices displayed include shipping, customs duties, tariffs, handling fees, and service fees.</li>
              <li>The final price shown at checkout is the total amount you will pay.</li>
              <li><strong>Prices are locked at the time of payment.</strong> Once payment is confirmed, the price cannot be changed.</li>
              <li>Currency conversion rates are locked at the time of order placement.</li>
            </ul>
          </section>

          <section className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">4. Customs, Duties &amp; Taxes (MANDATORY)</h2>
            <p className="text-slate-600 mb-4">
              <strong>Customs duties, import taxes, and related fees are mandatory on all international orders.</strong>
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>All orders are subject to customs inspection and assessment by destination country authorities.</li>
              <li>Estimated customs fees are included in your final price based on product category and weight.</li>
              <li>In rare cases, actual customs fees may differ from estimates due to final government assessment.</li>
              <li>You are responsible for complying with all import regulations of your destination country.</li>
              <li>Prohibited or restricted items cannot be imported and may be confiscated by customs authorities.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">5. Weight &amp; Dimension Adjustments</h2>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Shipping costs are calculated using the greater of actual weight or dimensional (volumetric) weight.</li>
              <li><strong>The weight used for pricing is the maximum of: actual weight, dimensional weight, or estimated weight.</strong></li>
              <li>Final charges may be adjusted if the actual weight or dimensions differ significantly from estimates.</li>
              <li>Dimensional weight is calculated as: (Length × Width × Height) ÷ 139 cubic inches per pound.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">6. Payments</h2>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Orders are processed only after payment is confirmed.</li>
              <li>We accept payment via PayPal, Bank Transfer, and FirstPay (where available).</li>
              <li>Bank transfers and manual payment methods require verification before order processing begins.</li>
              <li>Payment confirmation may take 1-3 business days for manual verification methods.</li>
            </ul>
          </section>

          <section className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">7. No Refund Policy</h2>
            <p className="text-slate-600 mb-4">
              <strong>All sales are final. No refunds will be issued once an item has been purchased on your behalf 
              from a third-party marketplace.</strong>
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Once we purchase a product on your behalf, the transaction is final.</li>
              <li>We cannot return items to international marketplaces on your behalf.</li>
              <li>Customs duties and shipping fees are non-refundable under any circumstances.</li>
              <li><strong>Exceptions:</strong> Refunds may be considered ONLY if:
                <ul className="list-disc pl-6 mt-2">
                  <li>The item was never ordered due to our error</li>
                  <li>The service was not rendered as described</li>
                  <li>We are unable to fulfill the order before purchase is made</li>
                </ul>
              </li>
            </ul>
            <p className="text-slate-600 mt-4">
              By placing an order, you acknowledge and accept this no-refund policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">8. Delivery Estimates</h2>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Delivery times are estimates only and are not guaranteed.</strong></li>
              <li>Estimated delivery times depend on marketplace processing, shipping carrier schedules, and customs clearance.</li>
              <li>Delays may occur due to customs inspection, weather, carrier issues, or other factors beyond our control.</li>
              <li>We are not responsible for delays caused by incorrect shipping information provided by the customer.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">9. Right to Refuse Service</h2>
            <p className="text-slate-600 mb-4">
              CheepSweepz Concierge reserves the right to refuse or cancel any order at our sole discretion for 
              reasons including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Suspected fraudulent activity</li>
              <li>Violation of these Terms &amp; Conditions</li>
              <li>Orders for prohibited or restricted items</li>
              <li>Orders that violate applicable laws or regulations</li>
              <li>Orders that cannot meet our minimum profitability requirements</li>
              <li>Technical errors in pricing or availability</li>
            </ul>
            <p className="text-slate-600 mt-4">
              If we refuse or cancel an order before purchase, any payment will be refunded.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-slate-600">
              To the maximum extent permitted by law, CheepSweepz Concierge shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages arising from your use of our services. Our 
              total liability shall not exceed the amount paid for the specific order in question.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">11. Governing Law</h2>
            <p className="text-slate-600">
              These Terms &amp; Conditions are governed by and construed in accordance with the laws of 
              <strong> Barbados</strong>. Any disputes arising from these terms shall be subject to the exclusive 
              jurisdiction of the courts of Barbados.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">12. Changes to Terms</h2>
            <p className="text-slate-600">
              We reserve the right to modify these Terms &amp; Conditions at any time. Changes will be posted on 
              this page with an updated revision date. Continued use of our services after changes constitutes 
              acceptance of the updated terms.
            </p>
          </section>

          <section className="mb-8 p-4 bg-slate-50 rounded-lg">
            <h2 className="text-xl font-semibold text-slate-900 mb-4">Contact Us</h2>
            <p className="text-slate-600">
              <strong>CheepSweepz Concierge</strong><br />
              Culloden Road, Bridgetown, St. Michael, Barbados<br />
              Phone: <a href="tel:+12468411248" className="text-orange-600 hover:underline">+1 246-841-1248</a> | <a href="tel:+12464273323" className="text-orange-600 hover:underline">+1 246-427-3323</a><br />
              Email: <a href="mailto:cheepsweepz@gmail.com" className="text-orange-600 hover:underline">cheepsweepz@gmail.com</a><br />
              Hours: Open 24 hours
            </p>
          </section>

          <p className="text-slate-500 text-sm mt-8">
            By using CheepSweepz Concierge, you acknowledge that you have read, understood, and agree to be 
            bound by these Terms &amp; Conditions.
          </p>
        </div>
      </div>
    </Layout>
  );
}
